import React from "react";
import { Col, Container, Row, Table } from "react-bootstrap";
import { useLoaderData } from "react-router";

import { sanitize } from "dompurify";

import { store } from "../..";
import "../../assets/css/majorybl/pages/tryouts.scss";
import {
    globalActions,
    selectSetting,
} from "../../features/global/globalSlice";
import { useAppSelector } from "../../hooks/reduxHooks";
import { getTryouts } from "../../lib/api";
import { defaultDateFormat, defaultTimeFormat } from "../../lib/utils";
import { IAPIResponse, ITryout } from "../../types/api";

export const loader = async () => {
    const settings = store.getState().global.settings;
    const promises = [];

    !settings.length &&
        promises.push(store.dispatch(globalActions.getSettings()));

    const [tryouts] = await Promise.all([getTryouts(), ...promises]);

    return tryouts;
};

const Tryouts = (): React.JSX.Element => {
    const { tryouts: tryoutList } = useLoaderData() as IAPIResponse & {
        tryouts: ITryout[];
    };

    const tryoutText = useAppSelector(state =>
        selectSetting(state, "majorybl_tryout_page_content"),
    );

    return (
        <div data-testid="tryoutsContent">
            <Container className="py-5 tryouts">
                <Row>
                    <Col>
                        <h2>Upcoming Tryouts</h2>
                        <div
                            className="no-bottom-pad"
                            dangerouslySetInnerHTML={{
                                __html: sanitize(tryoutText?.setting),
                            }}
                        />

                        {tryoutList.length !== 0 ? (
                            <Table
                                striped
                                bordered
                                responsive="lg"
                                className="tryoutsTable"
                            >
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Location</th>
                                        <th>Team/Organization</th>
                                        <th>Age</th>
                                        <th>Description</th>
                                        <th>Contact Info</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tryoutList
                                        .filter(t => {
                                            const now = new Date();
                                            return t.dates.every(
                                                d =>
                                                    now <
                                                    new Date(
                                                        `${d.date}T${d.startTime}`,
                                                    ),
                                            );
                                        })
                                        .map((t, i) => {
                                            let contactInfo: React.ReactNode[] =
                                                [];

                                            t.contactName &&
                                                contactInfo.push(
                                                    <b key="name">{`${t.contactName}`}</b>,
                                                );

                                            t.contactEmail &&
                                                contactInfo.push(
                                                    <React.Fragment key="email">
                                                        {contactInfo.length !==
                                                            0 && <br />}
                                                        <a
                                                            href={`mailto: ${t.contactEmail}`}
                                                        >
                                                            {t.contactEmail}
                                                        </a>
                                                    </React.Fragment>,
                                                );

                                            t.contactNumber &&
                                                contactInfo.push(
                                                    <React.Fragment key="phoneNumber">
                                                        {contactInfo.length !==
                                                            0 && <br />}
                                                        {t.contactNumber}
                                                    </React.Fragment>,
                                                );

                                            t.contactSite &&
                                                contactInfo.push(
                                                    <React.Fragment key="website">
                                                        {contactInfo.length !==
                                                            0 && <br />}
                                                        <a
                                                            href={
                                                                /http/.test(
                                                                    t.contactSite,
                                                                )
                                                                    ? t.contactSite
                                                                    : `http://${t.contactSite}`
                                                            }
                                                        >
                                                            visit website
                                                        </a>
                                                    </React.Fragment>,
                                                );
                                            return (
                                                <tr key={i}>
                                                    <td width="17%">
                                                        {t.dates.map(
                                                            (d, idx) => (
                                                                <React.Fragment
                                                                    key={`team-${t.id}-tryouts-date-${idx}`}
                                                                >
                                                                    {idx !==
                                                                        0 && (
                                                                        <br />
                                                                    )}
                                                                    <b>
                                                                        {defaultDateFormat.format(
                                                                            new Date(
                                                                                `${d.date}T00:00:00`,
                                                                            ),
                                                                        )}
                                                                    </b>{" "}
                                                                    {defaultTimeFormat.format(
                                                                        new Date(
                                                                            `${d.date}T${d.startTime}`,
                                                                        ),
                                                                    )}
                                                                    {d.endTime &&
                                                                        ` - ${defaultTimeFormat.format(
                                                                            new Date(
                                                                                `${d.date}T${d.endTime}`,
                                                                            ),
                                                                        )}`}
                                                                </React.Fragment>
                                                            ),
                                                        )}
                                                    </td>
                                                    <td width="10%">
                                                        {typeof t.field ===
                                                            "object" &&
                                                        t.field !== null &&
                                                        t.field.id !== null ? (
                                                            <React.Fragment>
                                                                {t.field?.name}
                                                                <br />
                                                                {
                                                                    t.field
                                                                        ?.city
                                                                },{" "}
                                                                {t.field?.state}
                                                            </React.Fragment>
                                                        ) : (
                                                            "No Location provided"
                                                        )}
                                                    </td>
                                                    <td>{t.team}</td>
                                                    <td>{t.age}</td>
                                                    <td width="28%">
                                                        {t.description ||
                                                            "No Description provided"}
                                                    </td>
                                                    <td width="12%">
                                                        {contactInfo.length !==
                                                        0
                                                            ? contactInfo
                                                            : "No contact info provided"}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </Table>
                        ) : (
                            <h5>No upcoming tryouts</h5>
                        )}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Tryouts;
